import Image from 'next/image';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import useLocale from 'next-translate/useTranslation';
import React, { useRef, useState, useCallback } from 'react';

function TiltCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [6, -6]), {
    stiffness: 200,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), {
    stiffness: 200,
    damping: 20,
  });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformPerspective: 800 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Interactive before/after comparison slider with a draggable divider.
 * Users can drag to reveal the sketch vs the photorealistic render.
 */
function ComparisonSlider() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const isDragging = useRef(false);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  return (
    <div
      ref={containerRef}
      className='group relative aspect-[4/3] w-full cursor-col-resize select-none overflow-hidden rounded-2xl shadow-2xl'
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
      onTouchMove={handleTouchMove}
      onClick={(e) => handleMove(e.clientX)}
    >
      {/* After (render) - full width background */}
      <Image
        src='/images/sketchup-after.jpeg'
        alt='Photorealistic render from sketch'
        fill
        className='object-cover'
        priority
      />

      {/* Before (sketch) - clipped to slider position */}
      <div
        className='absolute inset-0'
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <Image
          src='/images/sketchup.jpeg'
          alt='Original architectural sketch'
          fill
          className='object-cover'
          priority
        />
      </div>

      {/* Slider line + handle */}
      <div
        className='absolute top-0 bottom-0 z-10 w-0.5 bg-white shadow-lg'
        style={{ left: `${sliderPosition}%` }}
      >
        {/* Draggable handle */}
        <div className='absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-white/90 shadow-xl backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl'>
          <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            className='text-gray-700'
          >
            <path
              d='M8 12H4M4 12L7 9M4 12L7 15M16 12H20M20 12L17 9M20 12L17 15'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className='absolute bottom-4 left-4 z-20 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm'>
        Sketch
      </div>
      <div className='absolute bottom-4 right-4 z-20 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm'>
        AI Render
      </div>
    </div>
  );
}

export function SketchToRenderSection() {
  const { t } = useLocale('ai-interior');

  const features = [
    {
      icon: (
        <svg
          className='h-6 w-6'
          fill='none'
          viewBox='0 0 24 24'
          strokeWidth={1.5}
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128Zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42'
          />
        </svg>
      ),
      titleKey: 'sketch-feature-any-sketch',
      descKey: 'sketch-feature-any-sketch-desc',
    },
    {
      icon: (
        <svg
          className='h-6 w-6'
          fill='none'
          viewBox='0 0 24 24'
          strokeWidth={1.5}
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18'
          />
        </svg>
      ),
      titleKey: 'sketch-feature-ai-understands',
      descKey: 'sketch-feature-ai-understands-desc',
    },
    {
      icon: (
        <svg
          className='h-6 w-6'
          fill='none'
          viewBox='0 0 24 24'
          strokeWidth={1.5}
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15'
          />
        </svg>
      ),
      titleKey: 'sketch-feature-styles',
      descKey: 'sketch-feature-styles-desc',
    },
  ];

  return (
    <section className='relative overflow-hidden bg-gray-950 py-24 sm:py-32'>
      {/* Subtle grid background */}
      <div className='absolute inset-0 opacity-[0.03]'>
        <svg className='h-full w-full' aria-hidden='true'>
          <defs>
            <pattern
              id='sketch-grid'
              width={40}
              height={40}
              patternUnits='userSpaceOnUse'
            >
              <path d='M40 0H0V40' fill='none' stroke='white' strokeWidth='1' />
            </pattern>
          </defs>
          <rect width='100%' height='100%' fill='url(#sketch-grid)' />
        </svg>
      </div>

      {/* Gradient accents */}
      <div className='absolute -top-40 right-0 h-80 w-80 rounded-full bg-purple-600/20 blur-3xl' />
      <div className='absolute -bottom-40 left-0 h-80 w-80 rounded-full bg-indigo-600/20 blur-3xl' />

      <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
        {/* Header */}
        <div className='mx-auto max-w-3xl text-center'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className='inline-flex items-center rounded-full border border-purple-500/30 bg-purple-500/10 px-4 py-1.5 text-sm font-medium text-purple-300'>
              <span className='relative mr-2 flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75' />
                <span className='relative inline-flex h-2 w-2 rounded-full bg-purple-400' />
              </span>
              {t('sketch-badge')}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className='mt-6 text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl'
          >
            {t('sketch-heading')}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className='mt-6 text-lg leading-8 text-gray-400'
          >
            {t('sketch-description')}
          </motion.p>
        </div>

        {/* Interactive comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          viewport={{ once: true }}
          className='mx-auto mt-16 max-w-4xl'
        >
          <ComparisonSlider />
          <p className='mt-3 text-center text-sm text-gray-500'>
            {t('sketch-drag-hint')}
          </p>
        </motion.div>

        {/* Feature cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          viewport={{ once: true }}
          className='mx-auto mt-20 grid max-w-5xl gap-6 sm:grid-cols-3'
        >
          {features.map((feature, index) => (
            <TiltCard
              key={index}
              className='group rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all duration-300 hover:border-purple-500/30 hover:bg-white/10 hover:shadow-lg hover:shadow-purple-500/5'
            >
              <div className='mb-4 inline-flex rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 p-3 text-white shadow-lg shadow-purple-500/20'>
                {feature.icon}
              </div>
              <h3 className='text-lg font-bold text-white'>
                {t(feature.titleKey)}
              </h3>
              <p className='mt-2 text-sm leading-relaxed text-gray-400'>
                {t(feature.descKey)}
              </p>
            </TiltCard>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className='mt-16 text-center'
        >
          <a
            href='/login?preset=ai-interior-designer'
            className='group inline-flex items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.03] active:scale-[0.98]'
          >
            {t('sketch-cta')}
            <svg
              className='ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 7l5 5m0 0l-5 5m5-5H6'
              />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
